import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { SearchLg } from "@untitledui/icons";
import type { Key } from "react-aria-components";
import { ListBox } from "react-aria-components";
import { Input } from "@/components/base/input/input";
import { BadgeWithButton } from "@/components/base/badges/badges";
import { ModalOverlay as UntitledModalOverlay, Modal as UntitledModal, Dialog as UntitledDialog } from "@/components/application/modals/modal";
import { useCommandMenu } from "./use-command-menu";
import { performSearch, parseShortcut, getCurrentUserType, getAvailableShortcuts, type SearchResult, type DocumentGroup } from "./command-menu-search";
import { CommandMenuItem } from "./command-menu-item";
import { useTranslation } from "react-i18next";

interface CommandMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CommandMenu = ({ isOpen, onClose }: CommandMenuProps) => {
    const { t } = useTranslation(['common']);
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [searchResults, setSearchResults] = useState<{ results: SearchResult[]; documentGroups: DocumentGroup[] }>({
        results: [],
        documentGroups: [],
    });
    const inputRef = useRef<HTMLInputElement>(null);
    const listBoxRef = useRef<HTMLDivElement>(null);
    const { selectedEntity, setSelectedEntity } = useCommandMenu();
    const userType = getCurrentUserType();

    const entityLabels: Record<string, string> = {
        container: t('commandMenu.entities.container'),
        sender: t('commandMenu.entities.sender'),
        receiver: t('commandMenu.entities.receiver'),
        account: t('commandMenu.entities.account'),
        address: t('commandMenu.entities.address'),
        document: t('commandMenu.entities.document'),
    };

    // Preparazione items unificati per navigazione (risultati + container headers + documenti)
    type UnifiedItem = SearchResult | { id: string; type: "container-header"; containerId: string; containerNumber: string; containerUrl: string };
    const unifiedItems = useRef<UnifiedItem[]>([]);
    const allSelectableItems = useRef<SearchResult[]>([]);

    // Aggiorna unifiedItems quando cambiano i risultati
    useEffect(() => {
        const items: UnifiedItem[] = [];
        const selectable: SearchResult[] = [];

        // Aggiungi risultati normali
        items.push(...searchResults.results);
        selectable.push(...searchResults.results);

        // Aggiungi documenti raggruppati con header
        searchResults.documentGroups.forEach((group) => {
            // Aggiungi header container (non selezionabile con Enter, ma navigabile)
            items.push({
                id: `container-header-${group.containerId}`,
                type: "container-header",
                containerId: group.containerId,
                containerNumber: group.containerNumber,
                containerUrl: group.containerUrl,
            });
            // Aggiungi documenti
            items.push(...group.documents);
            selectable.push(...group.documents);
        });

        unifiedItems.current = items;
        allSelectableItems.current = selectable;
    }, [searchResults]);

    // Parse shortcut quando cambia la query
    useEffect(() => {
        if (query.trim()) {
            const parsed = parseShortcut(query);
            // Per gli admin, permetti solo il filtro "account"
            if (parsed.entity && parsed.entity !== selectedEntity) {
                if (userType === "ADMIN" && parsed.entity !== "account") {
                    // Ignora shortcut non permessi per admin
                    return;
                }
                setSelectedEntity(parsed.entity);
            }
        }
    }, [query, selectedEntity, setSelectedEntity, userType]);

    // Esegui ricerca quando cambia query o selectedEntity
    useEffect(() => {
        if (query.trim() || selectedEntity) {
            const results = performSearch(query, selectedEntity);
            // Valida e filtra risultati per assicurarsi che siano validi
            const validResults = results.results.filter((r) => r && r.id && r.title);
            const validDocumentGroups = results.documentGroups.map((group) => ({
                ...group,
                documents: group.documents.filter((doc) => doc && doc.id && doc.title),
            })).filter((group) => group.documents.length > 0);
            setSearchResults({ results: validResults, documentGroups: validDocumentGroups });
        } else {
            setSearchResults({ results: [], documentGroups: [] });
        }
    }, [query, selectedEntity]);

    // Focus sull'input quando si apre, reset completo quando si chiude
    useEffect(() => {
        if (isOpen && inputRef.current) {
            // Per gli admin, assicurati che il filtro sia valido
            if (userType === "ADMIN" && selectedEntity && selectedEntity !== "account") {
                setSelectedEntity(null);
            }
            setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
        } else {
            // Reset completo quando si chiude
            setQuery("");
            setSelectedEntity(null);
            // Rimuovi il focus dall'input
            if (inputRef.current) {
                inputRef.current.blur();
            }
            // Rimuovi il focus da qualsiasi elemento attivo
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        }
    }, [isOpen, setSelectedEntity, userType, selectedEntity]);

    // Navigazione unificata con frecce - listener globale quando il menu è aperto
    useEffect(() => {
        if (!isOpen) return;

        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            const activeElement = document.activeElement;
            const isInInput = activeElement === inputRef.current ||
                (activeElement instanceof HTMLElement && activeElement.closest('input') === inputRef.current);

            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                // Se siamo nell'input, gestisci diversamente
                if (isInInput) {
                    if (e.key === "ArrowDown") {
                        // Vai al primo elemento
                        e.preventDefault();
                        e.stopPropagation();
                        const firstItem = listBoxRef.current?.querySelector('[role="option"]:not([data-container-header]), button[data-container-header]') as HTMLElement;
                        if (firstItem) {
                            firstItem.focus();
                            firstItem.scrollIntoView({ block: "nearest" });
                        }
                    }
                    // ArrowUp nell'input non fa nulla
                    return;
                }

                // Se siamo nella lista, gestisci navigazione unificata
                const isInList = activeElement?.closest('[role="listbox"]') ||
                    activeElement?.closest('button[data-container-header]') ||
                    activeElement?.closest('[role="option"]');

                if (!isInList) return;

                e.preventDefault();
                e.stopPropagation();

                // Trova tutti gli elementi navigabili in ordine
                const allItems = Array.from(
                    listBoxRef.current?.querySelectorAll('[role="option"]:not([data-container-header]), button[data-container-header]') || []
                ) as HTMLElement[];

                if (allItems.length === 0) return;

                // Trova l'indice dell'elemento corrente
                let currentIndex = -1;
                const currentElement = activeElement as HTMLElement;

                for (let i = 0; i < allItems.length; i++) {
                    const item = allItems[i];
                    if (item === currentElement ||
                        item.contains(currentElement) ||
                        (currentElement.closest('[role="option"]') && item.closest('[role="option"]') === currentElement.closest('[role="option"]')) ||
                        (currentElement.closest('button[data-container-header]') && item === currentElement.closest('button[data-container-header]'))) {
                        currentIndex = i;
                        break;
                    }
                }

                if (currentIndex === -1) return;

                if (e.key === "ArrowUp") {
                    if (currentIndex > 0) {
                        // Vai all'elemento precedente
                        allItems[currentIndex - 1].focus();
                        allItems[currentIndex - 1].scrollIntoView({ block: "nearest" });
                    } else {
                        // Torna all'input
                        inputRef.current?.focus();
                    }
                } else if (e.key === "ArrowDown") {
                    if (currentIndex < allItems.length - 1) {
                        // Vai all'elemento successivo
                        allItems[currentIndex + 1].focus();
                        allItems[currentIndex + 1].scrollIntoView({ block: "nearest" });
                    }
                }
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => {
            window.removeEventListener("keydown", handleGlobalKeyDown, true);
        };
    }, [isOpen]);

    // Gestione selezione risultato
    const handleSelection = useCallback(
        (key: Key) => {
            const keyString = String(key);
            // Rimuovi il focus da tutti gli elementi prima di navigare
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement) {
                activeElement.blur();
            }

            // Chiudi il menu prima di navigare
            onClose();

            // Naviga dopo un piccolo delay per assicurarsi che il menu sia chiuso
            setTimeout(() => {
                // Cerca nei risultati normali
                const result = searchResults.results.find((r) => r.id === keyString);
                if (result) {
                    navigate(result.url);
                    return;
                }

                // Cerca nei documenti raggruppati
                for (const group of searchResults.documentGroups) {
                    const doc = group.documents.find((d) => d.id === keyString);
                    if (doc) {
                        navigate(doc.url);
                        return;
                    }

                    // Click sul container header
                    if (keyString === `container-header-${group.containerId}` || keyString === `container-${group.containerId}`) {
                        navigate(group.containerUrl);
                        return;
                    }
                }
            }, 0);
        },
        [searchResults, navigate, onClose]
    );

    // Rimuovi filtro
    const handleRemoveFilter = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setSelectedEntity(null);
            setQuery(query.replace(/^>\w+\s*/, "").trim()); // Rimuovi shortcut dalla query se presente
        },
        [query, setSelectedEntity]
    );


    // Gestione Escape con reset completo
    const handleEscape = useCallback(() => {
        // Reset completo dello stato
        setQuery("");
        setSelectedEntity(null);
        // Rimuovi il focus dall'input del menu
        if (inputRef.current) {
            inputRef.current.blur();
        }
        // Rimuovi il focus da qualsiasi elemento attivo, incluso l'input della sidebar
        setTimeout(() => {
            const activeElement = document.activeElement as HTMLElement;
            if (activeElement) {
                // Se è un input (potrebbe essere quello della sidebar), rimuovi il focus
                if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') {
                    activeElement.blur();
                }
                // Rimuovi il focus anche da altri elementi
                activeElement.blur();
            }
            // Assicurati che nessun elemento abbia il focus
            if (document.activeElement && document.activeElement !== document.body) {
                (document.activeElement as HTMLElement).blur();
            }
        }, 0);
        // Chiudi il menu
        onClose();
    }, [onClose, setSelectedEntity]);


    const hasResults = searchResults.results.length > 0 || searchResults.documentGroups.length > 0;
    const hasQuery = query.trim().length > 0;

    return (
        <UntitledModalOverlay
            isOpen={isOpen}
            onOpenChange={(open) => {
                if (!open) {
                    // Reset completo quando si chiude dall'overlay
                    setQuery("");
                    setSelectedEntity(null);
                    if (inputRef.current) {
                        inputRef.current.blur();
                    }
                    // Rimuovi il focus da qualsiasi elemento attivo, incluso l'input della sidebar
                    setTimeout(() => {
                        const activeElement = document.activeElement as HTMLElement;
                        if (activeElement) {
                            // Se è un input (potrebbe essere quello della sidebar), rimuovi il focus
                            if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') {
                                activeElement.blur();
                            }
                            activeElement.blur();
                        }
                        // Assicurati che nessun elemento abbia il focus
                        if (document.activeElement && document.activeElement !== document.body) {
                            (document.activeElement as HTMLElement).blur();
                        }
                    }, 0);
                    onClose();
                }
            }}
            isDismissable
        >
            <UntitledModal className="!w-auto min-w-[33vw] max-w-2xl">
                <UntitledDialog className="outline-hidden" aria-labelledby="command-menu-title">
                    <section className="relative flex min-h-0 flex-col outline-hidden w-full max-w-2xl">
                        {/* Input Section */}
                        <div className="react-aria-TextField" data-rac="">
                            <div
                                role="presentation"
                                className="flex items-center gap-x-2 rounded-xl bg-primary p-4 relative outline-hidden after:absolute after:bottom-0 after:left-0 after:h-px after:w-full"
                            >
                                <SearchLg className="pointer-events-none absolute left-4 size-5 text-fg-quaternary" aria-hidden="true" />
                                <Input
                                    ref={inputRef}
                                    placeholder={t('commandMenu.searchPlaceholder')}
                                    value={query}
                                    onChange={setQuery}
                                    onKeyDown={(e) => {
                                        if (e.key === "Escape") {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (e.nativeEvent instanceof KeyboardEvent && e.nativeEvent.stopImmediatePropagation) {
                                                e.nativeEvent.stopImmediatePropagation();
                                            }
                                            handleEscape();
                                        } else if (e.key === "Enter") {
                                            // Se c'è un item focalizzato o selezionato, selezionalo
                                            e.preventDefault();
                                            const focusedItem = listBoxRef.current?.querySelector('[role="option"]:focus:not([data-container-header])') as HTMLElement ||
                                                listBoxRef.current?.querySelector('button[data-container-header]:focus') as HTMLElement;
                                            const selectedItem = listBoxRef.current?.querySelector('[role="option"][aria-selected="true"]:not([data-container-header])') as HTMLElement;
                                            const targetItem = focusedItem || selectedItem;

                                            if (targetItem) {
                                                if (targetItem.hasAttribute('data-container-header')) {
                                                    // È un header container
                                                    const containerId = targetItem.getAttribute('data-container-id');
                                                    if (containerId) {
                                                        handleSelection(`container-${containerId}`);
                                                    }
                                                } else {
                                                    targetItem.click();
                                                }
                                            } else if (allSelectableItems.current.length > 0) {
                                                // Se non c'è selezione/focus, seleziona il primo risultato selezionabile
                                                handleSelection(allSelectableItems.current[0].id);
                                            }
                                        }
                                    }}
                                    className="w-full pl-11"
                                    shortcut={false}
                                    size="sm"
                                    icon={undefined}
                                />
                                <div className="flex items-center gap-x-1">
                                    {selectedEntity ? (
                                        <BadgeWithButton
                                            type="pill-color"
                                            color="brand"
                                            size="sm"
                                            onButtonClick={handleRemoveFilter}
                                            buttonLabel={t('commandMenu.removeFilter')}
                                        >
                                            {entityLabels[selectedEntity]}
                                        </BadgeWithButton>
                                    ) : (
                                        <div className="min-w-6 rounded-[4px] bg-secondary_alt px-1 py-0.5 text-center text-sm font-medium text-tertiary ring-1 ring-secondary ring-inset">
                                            ⌘K
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <h2 id="command-menu-title" slot="title" className="sr-only">
                            {t('commandMenu.title')}
                        </h2>

                        {/* Results Section */}
                        <div className="min-h-0 flex-1" ref={listBoxRef}>
                            <div className="flex max-h-[500px] flex-1 scroll-py-10 flex-col gap-2 overflow-auto focus:outline-hidden pt-2 bg-primary rounded-lg mx-2 mb-2">
                                {hasQuery ? (
                                    hasResults ? (
                                        <>
                                            {searchResults.results.length > 0 && (
                                                <section className="border-b border-secondary pb-2 last:border-transparent" role="group">
                                                    <ListBox
                                                        items={searchResults.results}
                                                        onAction={handleSelection}
                                                        selectionMode="single"
                                                        className="outline-hidden"
                                                        shouldFocusOnHover={false}
                                                    >
                                                        {(result: SearchResult) => (
                                                            <CommandMenuItem
                                                                result={result}
                                                                inputRef={inputRef}
                                                                listBoxRef={listBoxRef}
                                                            />
                                                        )}
                                                    </ListBox>
                                                </section>
                                            )}

                                            {searchResults.documentGroups.map((group) => (
                                                <section
                                                    key={group.containerId}
                                                    className="border-b border-secondary pb-2 last:border-transparent"
                                                    role="group"
                                                >
                                                    <div className="px-2 py-1.5">
                                                        <button
                                                            type="button"
                                                            data-container-header
                                                            data-container-id={group.containerId}
                                                            onClick={() => handleSelection(`container-header-${group.containerId}`)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    handleSelection(`container-header-${group.containerId}`);
                                                                } else if (e.key === "ArrowUp") {
                                                                    // Naviga all'elemento precedente
                                                                    e.preventDefault();
                                                                    const currentItem = e.currentTarget;
                                                                    const allItems = Array.from(
                                                                        listBoxRef.current?.querySelectorAll('[role="option"]:not([data-container-header]), button[data-container-header]') || []
                                                                    ) as HTMLElement[];
                                                                    const currentIndex = allItems.indexOf(currentItem);
                                                                    if (currentIndex > 0) {
                                                                        allItems[currentIndex - 1].focus();
                                                                        allItems[currentIndex - 1].scrollIntoView({ block: "nearest" });
                                                                    } else {
                                                                        // Torna all'input
                                                                        inputRef.current?.focus();
                                                                    }
                                                                } else if (e.key === "ArrowDown") {
                                                                    // Naviga all'elemento successivo
                                                                    e.preventDefault();
                                                                    const currentItem = e.currentTarget;
                                                                    const allItems = Array.from(
                                                                        listBoxRef.current?.querySelectorAll('[role="option"]:not([data-container-header]), button[data-container-header]') || []
                                                                    ) as HTMLElement[];
                                                                    const currentIndex = allItems.indexOf(currentItem);
                                                                    if (currentIndex < allItems.length - 1) {
                                                                        allItems[currentIndex + 1].focus();
                                                                        allItems[currentIndex + 1].scrollIntoView({ block: "nearest" });
                                                                    }
                                                                }
                                                            }}
                                                            className="w-full text-left px-2 py-1 text-xs font-semibold text-tertiary uppercase tracking-wide hover:text-secondary transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-focus-ring"
                                                        >
                                                            {t('commandMenu.entities.container')}: {group.containerNumber}
                                                        </button>
                                                    </div>
                                                    <ListBox
                                                        items={group.documents}
                                                        onAction={handleSelection}
                                                        selectionMode="single"
                                                        className="outline-hidden"
                                                        shouldFocusOnHover={false}
                                                    >
                                                        {(doc: SearchResult) => (
                                                            <CommandMenuItem
                                                                result={doc}
                                                                inputRef={inputRef}
                                                                listBoxRef={listBoxRef}
                                                            />
                                                        )}
                                                    </ListBox>
                                                </section>
                                            ))}
                                        </>
                                    ) : (
                                        <div className="py-16 px-4 text-center">
                                            <p className="text-sm text-tertiary">{t('commandMenu.noResults')}</p>
                                        </div>
                                    )
                                ) : (
                                    <div className="py-4 px-4">
                                        <div className="mb-3">
                                            <p className="text-sm font-medium text-secondary mb-2">{t('commandMenu.availableShortcuts', 'Available shortcuts:')}</p>
                                        </div>
                                        <div className="space-y-2">
                                            {getAvailableShortcuts(userType).map((shortcut) => (
                                                <div
                                                    key={shortcut.shortcut}
                                                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-primary_hover cursor-pointer transition-colors"
                                                    onClick={() => {
                                                        setQuery(shortcut.shortcut + " ");
                                                        // Focus sull'input dopo aver impostato lo shortcut
                                                        setTimeout(() => {
                                                            inputRef.current?.focus();
                                                        }, 0);
                                                    }}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <code className="px-2 py-1 bg-tertiary text-primary font-mono text-sm rounded">
                                                            {shortcut.shortcut}
                                                        </code>
                                                        <span className="text-sm text-primary">
                                                            {shortcut.description}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-4 pt-3 border-t border-secondary">
                                            <p className="text-xs text-tertiary text-center">
                                                {t('commandMenu.startTypingHint', 'Start typing to search or use shortcuts above')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </UntitledDialog>
            </UntitledModal>
        </UntitledModalOverlay>
    );
};

